import { all, call, put, takeLatest, takeLeading } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { TrackingDeviceAssignment } from '../../types/entity.types';
import { SagaPayload } from '../api.types';
import {
  CreateTrackingDeviceAssignmentRequest,
  CreateTrackingDeviceAssignmentResponse,
  RetrieveTrackingDeviceAssignmentsRequest,
  RetrieveTrackingDeviceAssignmentsResponse
} from './device-assignment.types';

import {
  CREATING_TRACKING_DEVICE_ASSIGNMENT,
  RETRIEVING_TRACKING_DEVICE_ASSIGNMENTS
} from './device-assignment.constants';

import {
  requestCreateTrackingDeviceAssignment,
  requestRetrieveTrackingDeviceAssignments
} from './device-assignment.actions';

import {
  getCreateTrackingDeviceAssignmentRequest,
  getRetrieveTrackingDeviceAssignmentsRequest
} from './device-assignment.endpoints';

import { updateMemberDeviceId } from '../../slices/programme/member/member.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR, SUCCESS } from '../../utils/constants';

export function* performCreateTrackingDeviceAssignmentRequest({
  payload
}: SagaPayload<CreateTrackingDeviceAssignmentRequest, TrackingDeviceAssignment>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_TRACKING_DEVICE_ASSIGNMENT));

    const [endpoint, options] = getCreateTrackingDeviceAssignmentRequest(requestData);
    const { data }: AxiosResponse<CreateTrackingDeviceAssignmentResponse> = yield call(axios, endpoint, options);

    yield put(
      updateMemberDeviceId({
        systemUserExternalReference: requestData.systemUserExternalReference,
        deviceId: requestData.deviceId
      })
    );

    if (onSuccess) {
      yield call(onSuccess, data.trackingDeviceAssignment);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(CREATING_TRACKING_DEVICE_ASSIGNMENT));
  }
}

export function* watchForCreateTrackingDeviceAssignmentRequest() {
  yield takeLatest(requestCreateTrackingDeviceAssignment.type, performCreateTrackingDeviceAssignmentRequest);
}

export function* performRetrieveTrackingDeviceAssignmentsRequest({
  payload
}: SagaPayload<RetrieveTrackingDeviceAssignmentsRequest, TrackingDeviceAssignment[]>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_TRACKING_DEVICE_ASSIGNMENTS));

    const [endpoint, options] = getRetrieveTrackingDeviceAssignmentsRequest(requestData);
    const { data }: AxiosResponse<RetrieveTrackingDeviceAssignmentsResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.trackingDeviceAssignments);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_TRACKING_DEVICE_ASSIGNMENTS));
  }
}

export function* watchForRetrieveTrackingDeviceAssignmentsRequest() {
  yield takeLeading(requestRetrieveTrackingDeviceAssignments.type, performRetrieveTrackingDeviceAssignmentsRequest);
}

export default function* deviceAssignmentSaga() {
  yield all([watchForCreateTrackingDeviceAssignmentRequest(), watchForRetrieveTrackingDeviceAssignmentsRequest()]);
}
